<template>
    <v-dialog v-model="modal" max-width="750" :fullscreen="$breakpoint.smAndDown" :retain-focus="false">
        <v-card>
            <v-card-title class="d-flex justify-space-between gc-2 text-wrap">
                {{ $t('send_email') }}
                <v-spacer />
                <v-icon data-cy="close" size="x-small" class="mt-2 ml-auto" @click="modal = false"> mdi-close </v-icon>
            </v-card-title>
            <v-card-text>
                <v-form v-model="valid" :disabled="loading" data-cy="email-form">
                    <v-select
                        v-model="templateId"
                        :items="templates"
                        :loading="templatesLoading"
                        :disabled="!templates.length || templatesLoading"
                        :label="$t('template')"
                        variant="outlined"
                        density="compact"
                        color="primary"
                        data-cy="template"
                        clearable
                        item-title="text"
                        @update:model-value="selectTemplate"
                    />
                    <v-text-field
                        v-model="message.subject"
                        :label="`${$t('email_form.subject')}*`"
                        variant="outlined"
                        density="compact"
                        color="primary"
                        data-cy="subject"
                        :rules="[requiredRule('email_form.subject')]"
                    />
                    <emails-field
                        v-model="message.to"
                        :label="`${$t('email_form.to')}${toFieldRequired ? '*' : ''}`"
                        :required="toFieldRequired"
                        variant="outlined"
                        density="compact"
                        color="primary"
                        data-cy="message-to"
                    />
                    <emails-field
                        v-model="message.cc"
                        :label="$t('email_form.cc')"
                        variant="outlined"
                        density="compact"
                        color="primary"
                        data-cy="message-cc"
                    />
                    <emails-field
                        v-model="message.bcc"
                        :label="`${$t('email_form.bcc')}${!toFieldRequired ? '*' : ''}`"
                        :required="!toFieldRequired"
                        variant="outlined"
                        density="compact"
                        color="primary"
                        data-cy="message-bcc"
                    />
                    <NewHtmlEditor
                        v-if="!templateId"
                        v-model="message.html"
                        :label="`${$t('email_form.text')}*`"
                        class="mb-4"
                    />
                    <v-textarea
                        v-model="message.html"
                        :rules="[...(!templateId ? [requiredRule('email_form.text')] : [])]"
                        class="d-none"
                        data-cy="message-html"
                    />
                    <v-file-input
                        v-model="attachments"
                        :label="$t('email_form.attachments')"
                        multiple
                        variant="outlined"
                        density="compact"
                        color="primary"
                        data-cy="attachments"
                        chips
                        counter
                        :hint="$t('hint.multiple_files')"
                        persistent-hint
                    />
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-btn data-cy="cancel" @click="modal = false">
                    {{ $t('cancel') }}
                </v-btn>
                <template v-if="templateId">
                    <v-btn
                        variant="flat"
                        size="large"
                        :disabled="!valid || loading || !selectedTemplate"
                        data-cy="edit"
                        @click="edit = true"
                    >
                        {{ $t('edit') }}
                    </v-btn>
                    <v-btn
                        variant="flat"
                        size="large"
                        color="secondary"
                        :disabled="!valid || loading || !selectedTemplate"
                        data-cy="preview"
                        @click="preview = true"
                    >
                        {{ $t('preview') }}
                    </v-btn>
                </template>
                <v-btn
                    variant="flat"
                    color="primary"
                    :disabled="!valid || loading || (templateId && !selectedTemplate)"
                    :loading="loading"
                    data-cy="send"
                    @click="send"
                >
                    {{ $t('send') }}
                </v-btn>
            </v-card-actions>
        </v-card>
        <email-preview-modal v-model="preview" :html="emailHtml" />
        <email-edit-modal v-model="edit" :json="emailJson" @change="editedTemplateHtml = $event" />
    </v-dialog>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import EmailsField from '@/components/snippets/forms/EmailsField.vue'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import {blobToBase64} from '@/utils/helpers'
    import EmailPreviewModal from '@/components/account/modals/EmailPreviewModal.vue'
    import EmailEditModal from '@/components/account/modals/EmailEditModal.vue'
    import Handlebars from 'handlebars'
    import {wrapHtmlMail} from '~/utils/email'

    const message = () => ({
        subject: '',
        to: [],
        cc: [],
        bcc: [],
        html: '',
        attachments: [],
    })

    // TODO Need refactoring with SendEmailModal
    @Component({
        components: {
            EmailEditModal,
            EmailPreviewModal,
            EmailsField,
            NewHtmlEditor: defineAsyncComponent(() => import('@/components/admin/NewHtmlEditor.vue')),
        },
    })
    export class SendClientsEmailModal extends mixins(FormValidationRulesMixin) {
        @VModel() modal
        @Prop() emails

        valid = false
        loading = false
        message = message()
        attachments = []
        templateId = null
        templates = []
        preview = false
        edit = false
        editedTemplateHtml = null
        templatesLoading = false
        selectedTemplate = null

        @Emit()
        sent() {}

        @Watch('modal')
        async toggleModal(modal) {
            this.message = message()
            if (this.emails.length === 1) {
                this.message.to = this.emails
            } else {
                this.message.bcc = this.emails
            }
            if (modal && !this.templates.length) {
                try {
                    this.templatesLoading = true
                    const {templates} = await this.$api.notificationTemplates.get({skipTemplate: true})
                    this.templates = templates
                        .filter(({notificationType}) => ['DEFAULT_SIGNATURE'].includes(notificationType))
                        .map(({id, notificationType, recipientType, htmlTemplate, jsonTemplate, info}) => ({
                            value: id,
                            text: info || this.$t(`admin.notificationType.${notificationType}`),
                            recipientType,
                            htmlTemplate,
                            jsonTemplate,
                        }))
                } catch (e) {
                    this.templates = []
                } finally {
                    this.templatesLoading = false
                }
            }
        }

        async selectTemplate(id) {
            if (id && this.selectedTemplate?.id !== id) {
                try {
                    this.templatesLoading = true
                    const {
                        templates: [selectedTemplate],
                    } = await this.$api.notificationTemplates.get({id})
                    this.selectedTemplate = selectedTemplate
                } catch (e) {
                    this.selectedTemplate = null
                } finally {
                    this.templatesLoading = false
                }
            }
        }

        async send() {
            if (!this.valid || this.loading) return
            try {
                this.loading = true
                this.message.attachments = await Promise.all(
                    this.attachments.map(async file => {
                        const path = await blobToBase64(file)
                        return {
                            path,
                            filename: file.name,
                        }
                    })
                )
                const html = this.templateId ? this.emailHtml : wrapHtmlMail(this.message.html)
                await this.$api.mail.post({message: {...this.message, html}})
                this.$toast.success(this.$t('success_message.send_email'))
                this.modal = false
                this.message = message()
                this.sent()
            } catch (e) {
                this.$toast.error(this.$t('error_message.send_email_error'))
            } finally {
                this.loading = false
            }
        }

        get emailHtml() {
            if (!this.selectedTemplate) return null
            const htmlTemplate = this.editedTemplateHtml || this.selectedTemplate.htmlTemplate
            const template = Handlebars.compile(htmlTemplate)
            return template({userName: `${authStore.person.firstName || ''} ${authStore.person.lastName || ''}`.trim()})
        }

        get emailJson() {
            if (!this.selectedTemplate) return null
            return this.selectedTemplate.jsonTemplate
        }

        get toFieldRequired() {
            return this.message.bcc.length === 0
        }
    }

    export default SendClientsEmailModal
</script>
